import { useTranslation } from 'react-i18next';

const clusterImg = 'http://geel.fp.08wx.cn/images/c40316e0c4d40311987cd6256dec0658.jpg';

const COPY = {
  'zh-CN': {
    title: '集群版',
    highlight: '企业级弹性扩容',
    desc: '专为大型交易所或快速增长业务设计，基于 Kubernetes 云原生架构，支持全球多地域多活部署，系统可用性达 99.99%，轻松应对用户量级跃升。',
    peakLabel: '峰值撮合能力',
    peakUnit: 'Orders / Second',
    stackTitle: '核心技术栈',
    regions: ['北美', '欧洲', '亚太', '中东'],
    features: [
      { icon: 'ri-expand-diagonal-line', title: '弹性水平扩容', desc: '基于 K8s 容器化编排，负载峰值时秒级自动扩容，低峰时自动缩容降低成本', color: '#FF2D78' },
      { icon: 'ri-global-line', title: '全球多节点部署', desc: '北美、欧洲、亚太、中东多地域节点，就近接入，延迟降低 70%', color: '#FF8C42' },
      { icon: 'ri-speed-fill', title: '百万级撮合 TPS', desc: '分布式撮合引擎，单集群支持 1,000,000+ 订单/秒，峰值无感切换', color: '#FF2D78' },
      { icon: 'ri-shield-flash-line', title: '多活容灾架构', desc: '双活/三活数据中心，RTO &lt; 30 秒，RPO &lt; 5 秒，业务不中断', color: '#FF8C42' },
      { icon: 'ri-refresh-line', title: '热更新零停机', desc: '滚动升级策略，功能模块独立发布，不影响正在进行中的交易', color: '#FF2D78' },
      { icon: 'ri-dashboard-line', title: '全链路监控', desc: 'Prometheus + Grafana 可视化，异常告警响应 &lt; 1 分钟，全链路追踪', color: '#FF8C42' },
    ],
  },
  en: {
    title: 'Cluster Edition',
    highlight: 'Enterprise Elastic Scaling',
    desc: 'Built for large exchanges and fast-growing businesses on Kubernetes cloud-native architecture, with global multi-region active-active deployment and 99.99% availability.',
    peakLabel: 'Peak Matching Capacity',
    peakUnit: 'Orders / Second',
    stackTitle: 'Core Tech Stack',
    regions: ['North America', 'Europe', 'APAC', 'Middle East'],
    features: [
      { icon: 'ri-expand-diagonal-line', title: 'Elastic Horizontal Scaling', desc: 'Auto scale-out in seconds during peaks and scale-in off-peak to control infrastructure cost.', color: '#FF2D78' },
      { icon: 'ri-global-line', title: 'Global Multi-Node Deployment', desc: 'Nodes across NA/EU/APAC/ME for nearby access and up to 70% lower latency.', color: '#FF8C42' },
      { icon: 'ri-speed-fill', title: 'Million-Level Matching TPS', desc: 'Distributed matching engine supports 1,000,000+ orders/sec per cluster.', color: '#FF2D78' },
      { icon: 'ri-shield-flash-line', title: 'Multi-Active DR Architecture', desc: 'Dual/triple-active data centers with RTO &lt; 30s and RPO &lt; 5s.', color: '#FF8C42' },
      { icon: 'ri-refresh-line', title: 'Zero-Downtime Hot Updates', desc: 'Rolling upgrades with independently released modules during live trading.', color: '#FF2D78' },
      { icon: 'ri-dashboard-line', title: 'Full-Link Monitoring', desc: 'Prometheus + Grafana observability with sub-minute alert response.', color: '#FF8C42' },
    ],
  },
  'zh-TW': {
    title: '集群版',
    highlight: '企業級彈性擴容',
    desc: '專為大型交易所或快速增長業務設計，基於 Kubernetes 雲原生架構，支持全球多地域多活部署，系統可用性達 99.99%，輕鬆應對用戶量級躍升。',
    peakLabel: '峰值撮合能力',
    peakUnit: 'Orders / Second',
    stackTitle: '核心技術棧',
    regions: ['北美', '歐洲', '亞太', '中東'],
    features: [
      { icon: 'ri-expand-diagonal-line', title: '彈性水平擴容', desc: '基於 K8s 容器化編排，負載峰值時秒級自動擴容，低峰時自動縮容降低成本', color: '#FF2D78' },
      { icon: 'ri-global-line', title: '全球多節點部署', desc: '北美、歐洲、亞太、中東多地域節點，就近接入，延遲降低 70%', color: '#FF8C42' },
      { icon: 'ri-speed-fill', title: '百萬級撮合 TPS', desc: '分布式撮合引擎，單集群支持 1,000,000+ 訂單/秒，峰值無感切換', color: '#FF2D78' },
      { icon: 'ri-shield-flash-line', title: '多活容災架構', desc: '雙活/三活數據中心，RTO &lt; 30 秒，RPO &lt; 5 秒，業務不中斷', color: '#FF8C42' },
      { icon: 'ri-refresh-line', title: '熱更新零停機', desc: '滾動升級策略，功能模塊獨立發布，不影響正在進行中的交易', color: '#FF2D78' },
      { icon: 'ri-dashboard-line', title: '全鏈路監控', desc: 'Prometheus + Grafana 可視化，異常告警響應 &lt; 1 分鐘，全鏈路追蹤', color: '#FF8C42' },
    ],
  },
} as const;

const techStack = ['Kubernetes', 'Docker', 'Redis Cluster', 'Kafka', 'TiDB', 'Nginx', 'Prometheus', 'Grafana', 'Elasticsearch'];

function getLocale(lng: string) {
  if (lng.startsWith('en')) return 'en';
  if (lng.startsWith('zh-TW')) return 'zh-TW';
  return 'zh-CN';
}

export default function CexClusterSection() {
  const { i18n } = useTranslation();
  const copy = COPY[getLocale(i18n.resolvedLanguage || i18n.language || 'zh-CN')];

  return (
    <section id="cluster" className="bg-light-section py-24 border-y border-primary/10">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 xl:gap-20 items-start">
          <div className="relative rounded-2xl overflow-hidden border border-primary/15">
            <div className="w-full h-[380px]">
              <img src={clusterImg} alt="CEX Cluster Architecture" className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
            </div>
            <div className="absolute top-5 left-5 bg-white/95 border border-primary/20 rounded-xl px-4 py-3 backdrop-blur-sm">
              <div className="text-xs text-gray-400 mb-0.5">{copy.peakLabel}</div>
              <div className="text-2xl font-heading font-black text-primary">1,000,000+</div>
              <div className="text-xs text-gray-400">{copy.peakUnit}</div>
            </div>
            <div className="absolute bottom-5 right-5 flex items-center gap-3">
              {copy.regions.map((region, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  <span className="text-gray-500 text-xs">{region}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full">
              <i className="ri-cloud-line text-primary text-xs" />
              <span className="text-primary text-xs font-medium uppercase tracking-widest">Cluster Edition</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 leading-tight">
              {copy.title}
              <br />
              <span className="text-primary">{copy.highlight}</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8">{copy.desc}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {copy.features.map((f, i) => (
                <div key={i} className="bg-white border border-primary/10 rounded-xl p-4 hover:border-primary/25 transition-all duration-200">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg mb-3" style={{ background: `${f.color}12` }}>
                    <i className={`${f.icon} text-sm`} style={{ color: f.color }} />
                  </div>
                  <h4 className="text-gray-800 text-sm font-medium mb-1">{f.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed" dangerouslySetInnerHTML={{ __html: f.desc }} />
                </div>
              ))}
            </div>

            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-3">{copy.stackTitle}</div>
              <div className="flex flex-wrap gap-2">
                {techStack.map((tech, i) => (
                  <span key={i} className="px-3 py-1.5 bg-white border border-primary/15 text-gray-500 text-xs rounded-lg">{tech}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
